import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ClassesEntity } from '../../../../../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../../../injection-tokens/entity-service-tokens';
import { ExportService } from '../../../../../../../../services/export.service';
import { ConfirmDialogService } from '../../../../../../../../services/confirm-dialog.service';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../../../../../../shared/pagination-options';

@Component({
  selector: 'app-user-vms-sub-tab',
  templateUrl: './user-vms-sub-tab.component.html',
  styleUrls: ['./user-vms-sub-tab.component.css'],
})
export class UserVmsSubTabComponent implements OnChanges, OnDestroy {
  @Input() classId!: string;
  @Input() classEntity!: ClassesEntity;
  @Input() canDestroyResources = false;
  @Input() userVms: any[] = [];
  @Input() isLoading = false;
  @Input() loadError: string | null = null;
  @Input() exportService!: ExportService;
  /** powerState dict from parent: userId → 'PowerState/running' | 'PowerState/deallocated' | etc. */
  @Input() vmPowerStates: Record<string, string> = {};

  @Output() refreshRequested = new EventEmitter<void>();

  /** Setter-based wiring so the paginator connects even when the table is
   * inside an `*ngIf` block (avoids the null-paginator issue with
   * `ngAfterViewInit` + deferred rendering). */
  @ViewChild(MatPaginator) set paginator(p: MatPaginator) {
    if (p) this.dataSource.paginator = p;
  }

  readonly displayedColumns = [
    'participant', 'loginId', 'subscription', 'resourceGroup',
    'vmDetails', 'connectionDetails', 'powerState', 'actions',
  ];
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;

  dataSource = new MatTableDataSource<any>([]);

  /** Tracks which VM userId has an in-flight action (disables that row's menu). */
  busyVmIds = new Set<string>();
  /** True while a bulk action is running. */
  isBulkBusy = false;

  /** Row currently showing the VM-details popover (null = none). */
  vmPopoverRow: any = null;
  vmPopoverPos: { top: number; left: number } = { top: 0, left: 0 };
  /** Row currently showing the connection-details popover (null = none). */
  connPopoverRow: any = null;
  connPopoverPos: { top: number; left: number } = { top: 0, left: 0 };

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly confirmDialog: ConfirmDialogService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userVms']) {
      this.dataSource.data = this.userVms ?? [];
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  truncate(val: string | null | undefined, len = 20): string {
    if (!val) return '—';
    return val.length > len ? val.slice(0, len) + '…' : val;
  }

  /** Extracts the short resource name from a full Azure ARM resource ID. */
  armName(id: string | null | undefined): string {
    if (!id) return '—';
    return id.split('/').pop() || id;
  }

  private todayStr(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private vmCount(): number {
    return this.dataSource.data.length;
  }

  isBusy(vm: any): boolean {
    return this.busyVmIds.has(vm.userId ?? vm.id);
  }

  private markBusy(vm: any): void { this.busyVmIds.add(vm.userId ?? vm.id); }
  private clearBusy(vm: any): void { this.busyVmIds.delete(vm.userId ?? vm.id); }

  getPowerState(vm: any): string {
    return this.vmPowerStates[vm.userId] ?? vm.powerState ?? 'unknown';
  }

  powerStateLabel(vm: any): string {
    const s = this.getPowerState(vm);
    if (s.includes('running')) return 'Running';
    if (s.includes('deallocated')) return 'Deallocated';
    if (s.includes('stopped')) return 'Stopped';
    if (s.includes('starting')) return 'Starting…';
    if (s.includes('stopping')) return 'Stopping…';
    if (s.includes('deallocating')) return 'Deallocating…';
    return '—';
  }

  powerStateClass(vm: any): string {
    const s = this.getPowerState(vm);
    if (s.includes('running')) return 'ps-running';
    if (s.includes('deallocated') || s.includes('stopped')) return 'ps-stopped';
    if (s.includes('starting') || s.includes('stopping') || s.includes('deallocating')) return 'ps-transitioning';
    return 'ps-unknown';
  }

  private notify(msg: string, type: 'success' | 'error' | 'info' = 'success'): void {
    this.snackBar.open(msg, 'Dismiss', {
      duration: 4000,
      panelClass: type === 'error' ? ['snack-error'] : type === 'info' ? ['snack-info'] : [],
    });
  }

  // ---------------------------------------------------------------------------
  // Subscription / VM helpers
  // ---------------------------------------------------------------------------

  private get vmSubscriptions(): any[] {
    return this.classEntity?.data?.['VMSubscription']
      ?? this.classEntity?.data?.['VmSubscription']
      ?? [];
  }

  private findSub(subscriptionId: string): any {
    return this.vmSubscriptions.find(
      s => (s.subscriptionId ?? s.SubscriptionId ?? '') === subscriptionId
    );
  }

  getSubscriptionName(vm: any): string {
    const sub = this.findSub(vm.subscriptionId);
    return sub?.displayName ?? sub?.DisplayName ?? vm.subscriptionId ?? '—';
  }

  getSubscriptionTooltip(vm: any): string {
    const sub = this.findSub(vm.subscriptionId);
    const id = vm.subscriptionId ?? '';
    const loc = sub?.location ?? sub?.Location ?? '';
    return loc ? `ID: ${id}\nLocation: ${loc}` : `ID: ${id}`;
  }

  // ---------------------------------------------------------------------------
  // Popover state helpers
  // ---------------------------------------------------------------------------

  showVmPopover(row: any, event: MouseEvent): void {
    this.vmPopoverRow = row;
    this.vmPopoverPos = this.calcPopoverPos(event.currentTarget as HTMLElement, 320);
  }
  hideVmPopover(): void { this.vmPopoverRow = null; }

  showConnPopover(row: any, event: MouseEvent): void {
    this.connPopoverRow = row;
    this.connPopoverPos = this.calcPopoverPos(event.currentTarget as HTMLElement, 240);
  }
  hideConnPopover(): void { this.connPopoverRow = null; }

  private calcPopoverPos(el: HTMLElement, panelWidth: number): { top: number; left: number } {
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const PANEL_HEIGHT = 140;

    let top = rect.bottom + 4;
    let left = rect.left;

    if (top + PANEL_HEIGHT > vh - 8) top = rect.top - PANEL_HEIGHT - 4;
    if (left + panelWidth > vw - 8) left = rect.right - panelWidth;
    left = Math.max(8, left);
    top = Math.max(8, top);

    return { top, left };
  }

  // ---------------------------------------------------------------------------
  // CSV Export
  // ---------------------------------------------------------------------------

  exportCsv(): void {
    const className = (this.classEntity as any)?.name ?? this.classId;
    const rows = this.dataSource.data.map(vm => ({
      'Participant': vm.participant ?? '',
      'Login Id': vm.redeemEmail ?? '',
      'VM Login (Username)': vm.adminUsername ?? '',
      'VM Password': vm.adminPassword ?? '',
      'Subscription Name': this.getSubscriptionName(vm),
      'Subscription Id': vm.subscriptionId ?? '',
      'Resource Group': vm.resourceGroupName ?? '',
      'VM Name': vm.vmName ?? '',
      'VM Id': this.armName(vm.vmId),
      'OS Disk': vm.osDiskName ?? this.armName(vm.osDiskId),
      'OS Disk Id': this.armName(vm.osDiskId),
      'NIC Name': vm.networkInterfaceName ?? '',
      'NIC Id': this.armName(vm.networkInterfaceId),
      'Public IP Name': vm.publicIpName ?? '',
      'Public IP Id': this.armName(vm.publicIpId),
      'IP Address': vm.publicIpAddress ?? '',
    }));
    this.exportService.toCsv(rows, `UserVMs_${className}_${this.classId}_${this.todayStr()}`);
  }

  // ---------------------------------------------------------------------------
  // Per-row power actions
  // ---------------------------------------------------------------------------

  startVM(vm: any): void {
    this.confirmDialog.typedConfirm({
      title: `Start VM — ${vm.participant ?? vm.vmName}`,
      message: `Start VM "${vm.vmName}". Azure compute charges will resume while the VM is running.`,
      type: 'info',
      confirmPhrase: 'START',
      confirmButtonText: 'Start VM',
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      this.markBusy(vm);
      this.classService.startVM(this.classId, vm.userId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.clearBusy(vm); this.notify(`VM "${vm.vmName}" is starting.`); this.refreshRequested.emit(); },
          error: () => { this.clearBusy(vm); this.notify(`Failed to start VM "${vm.vmName}".`, 'error'); },
        });
    });
  }

  shutdownVM(vm: any): void {
    this.confirmDialog.typedConfirm({
      title: `Shutdown VM — ${vm.participant ?? vm.vmName}`,
      message: `Gracefully shuts down the guest OS on "${vm.vmName}". The VM stays allocated — Azure compute charges continue.`,
      type: 'warning',
      confirmPhrase: 'SHUTDOWN',
      confirmButtonText: 'Shutdown VM',
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      this.markBusy(vm);
      this.classService.shutdownVM(this.classId, vm.userId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.clearBusy(vm); this.notify(`VM "${vm.vmName}" is shutting down.`); this.refreshRequested.emit(); },
          error: () => { this.clearBusy(vm); this.notify(`Failed to shutdown VM "${vm.vmName}".`, 'error'); },
        });
    });
  }

  deallocateVM(vm: any): void {
    this.confirmDialog.typedConfirm({
      title: `Deallocate VM — ${vm.participant ?? vm.vmName}`,
      message: `Stops and deallocates "${vm.vmName}" without a guest OS signal. Azure compute charges pause; storage charges continue.`,
      type: 'warning',
      confirmPhrase: 'DEALLOCATE',
      confirmButtonText: 'Deallocate VM',
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      this.markBusy(vm);
      this.classService.deallocateVM(this.classId, vm.userId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.clearBusy(vm); this.notify(`VM "${vm.vmName}" is being deallocated.`); this.refreshRequested.emit(); },
          error: () => { this.clearBusy(vm); this.notify(`Failed to deallocate VM "${vm.vmName}".`, 'error'); },
        });
    });
  }

  // ---------------------------------------------------------------------------
  // Per-row destroy actions (gated by canDestroyResources)
  // ---------------------------------------------------------------------------

  destroyVM(vm: any): void {
    const diskName = vm.osDiskName || vm.osDiskId || null;
    this.confirmDialog.typedConfirm({
      title: `Destroy VM — ${vm.participant ?? vm.vmName}`,
      message: `Permanently destroys VM "${vm.vmName}". This also removes the associated NIC, Public IP, and NSG entries. ` +
               `This action cannot be undone.`,
      details: [
        ...(vm.participant      ? [`Participant: ${vm.participant}`]         : []),
        `VM Name: ${vm.vmName ?? '—'}`,
        ...(vm.publicIpAddress  ? [`IP Address: ${vm.publicIpAddress}`]     : []),
        ...(diskName            ? [`OS Disk: ${diskName}`]                  : []),
      ],
      type: 'error',
      confirmPhrase: 'DESTROY VM',
      confirmSummary: diskName
        ? `VM only — OS Disk "${diskName}" will be RETAINED.`
        : 'VM will be deleted. The OS disk (if any) will be retained.',
      confirmButtonText: 'Destroy VM',
      secondaryOption: {
        phrase: 'DESTROY VM WITH DISK',
        buttonText: 'Destroy VM + OS Disk',
        confirmSummary: diskName
          ? `VM AND OS Disk "${diskName}" will be PERMANENTLY DELETED.`
          : 'VM and all associated OS disk resources will be permanently deleted.',
        buttonColorClass: 'btn-danger-red',
      },
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      const withDisk = result.selectedOption === 'secondary';
      this.markBusy(vm);
      this.classService.destroyVM(this.classId, vm.userId, withDisk)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.clearBusy(vm); this.notify(`VM "${vm.vmName}" destroyed.`); this.refreshRequested.emit(); },
          error: () => { this.clearBusy(vm); this.notify(`Failed to destroy VM "${vm.vmName}".`, 'error'); },
        });
    });
  }

  // ---------------------------------------------------------------------------
  // Bulk actions
  // ---------------------------------------------------------------------------

  startAll(): void {
    this.confirmDialog.typedConfirm({
      title: `Start All VMs`,
      message: `Start all ${this.vmCount()} VM(s) in this class. Azure compute charges will resume for all VMs.`,
      type: 'info',
      confirmPhrase: 'START ALL',
      confirmButtonText: 'Start All VMs',
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      this.isBulkBusy = true;
      this.classService.startAllVMs(this.classId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.isBulkBusy = false; this.notify('All VMs are starting.'); this.refreshRequested.emit(); },
          error: () => { this.isBulkBusy = false; this.notify('Failed to start all VMs.', 'error'); },
        });
    });
  }

  shutdownAll(): void {
    this.confirmDialog.typedConfirm({
      title: `Shutdown All VMs`,
      message: `Gracefully shuts down the guest OS on all ${this.vmCount()} VM(s) in this class. VMs stay allocated — Azure compute charges continue.`,
      type: 'warning',
      confirmPhrase: 'SHUTDOWN ALL',
      confirmButtonText: 'Shutdown All VMs',
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      this.isBulkBusy = true;
      this.classService.shutdownAllVMs(this.classId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.isBulkBusy = false; this.notify('All VMs are shutting down.'); this.refreshRequested.emit(); },
          error: () => { this.isBulkBusy = false; this.notify('Failed to shutdown all VMs.', 'error'); },
        });
    });
  }

  deallocateAll(): void {
    this.confirmDialog.typedConfirm({
      title: `Deallocate All VMs`,
      message: `Stops and deallocates all ${this.vmCount()} VM(s) in this class without a guest OS signal. Azure compute charges pause.`,
      type: 'warning',
      confirmPhrase: 'DEALLOCATE ALL',
      confirmButtonText: 'Deallocate All VMs',
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      this.isBulkBusy = true;
      this.classService.deallocateAllVMs(this.classId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.isBulkBusy = false; this.notify('All VMs are being deallocated.'); this.refreshRequested.emit(); },
          error: () => { this.isBulkBusy = false; this.notify('Failed to deallocate all VMs.', 'error'); },
        });
    });
  }

  destroyAllVMs(): void {
    const vms = this.dataSource.data;
    this.confirmDialog.typedConfirm({
      title: `Destroy All VMs`,
      message: `Permanently destroys all ${vms.length} VM(s) in this class, including their associated NICs, Public IPs, and NSG entries. ` +
               `This action cannot be undone.`,
      type: 'error',
      confirmPhrase: 'DESTROY ALL',
      confirmSummary: `All ${vms.length} VM(s) will be deleted. OS Disks will be RETAINED.`,
      confirmButtonText: 'Destroy All VMs',
      secondaryOption: {
        phrase: 'DESTROY ALL WITH DISKS',
        buttonText: 'Destroy All VMs + OS Disks',
        confirmSummary: `All ${vms.length} VM(s) AND their OS Disks will be PERMANENTLY DELETED.`,
        buttonColorClass: 'btn-danger-red',
      },
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      const withDisk = result.selectedOption === 'secondary';
      this.isBulkBusy = true;
      const calls = vms.map(vm => this.classService.destroyVM(this.classId, vm.userId, withDisk));
      forkJoin(calls)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.isBulkBusy = false; this.notify(`All ${vms.length} VMs destroyed.`); this.refreshRequested.emit(); },
          error: () => { this.isBulkBusy = false; this.notify('Some VM destructions failed. Refresh to see current state.', 'error'); this.refreshRequested.emit(); },
        });
    });
  }
}

